import Log from "../../../../Logger";

import { mutation, query } from "./api-core";

function getEwbReqId(resp: any) {
    return resp?.headers?.["ricoh-ewb-request-id"] ?? "-";
}

export type FeatureState = "ENABLED" | "DISABLED";

export type LinkServiceTokenExpiredDate = {
    accessTokenExpiredDate: string,
    refreshTokenExpiredDate: string
}

export const defaultPastDate: LinkServiceTokenExpiredDate = {
    accessTokenExpiredDate: "2000-01-01T23:59:59Z",
    refreshTokenExpiredDate: "2000-01-01T23:59:59Z"
}

type LinkServiceTokenGeneratedFromCode = {
    accessToken: {
        token: string
        expiredDate: string
    }
    refreshToken: {
        token: string
        expiredDate: string
    }
}

type LinkServiceToken = {
    token: string
    expiredDate: string
}

type KintoneSetting = {
    endpointUrl: string
    clientId: string
}

/**
 * kintoneのテナント設定を連携サービステーブルから削除する関数
 */
export async function deleteKintoneSetting(): Promise<boolean> {
    const logger = Log.getLogger();

    logger.api.info("Lantana-API", "Start to request: deleteKintoneSetting");

    const resp = await mutation<boolean>("deleteKintoneSetting", "DeleteKintoneSettingInput", {}, []);
    if (resp.data.errors && resp.data.errors.length && resp.data.errors.length > 0) {
        const msg = `Failed to execute deleteKintoneSetting API: id=${getEwbReqId(resp)}`;
        logger.api.error("Lantana-API", msg, resp.headers);
        throw new Error(msg);
    }

    logger.api.info("Lantana-API", "Succeed to request: deleteKintoneSetting", resp.headers);

    return resp.data.data.deleteKintoneSetting;
}

/**
 * kintoneのテナント設定を連携サービステーブルに保存する関数
 * ユーザーの入力値を保存する(暗号化処理はAPI内で行っている)
 */
export async function registerKintoneSetting(clientId: string, secretId: string, endpointUrl: string): Promise<boolean> {
    const logger = Log.getLogger();

    logger.api.info("Lantana-API", "Start to request: registerKintoneSetting");

    const resp = await mutation<boolean>("registerKintoneSetting", "RegisterKintoneSettingInput", { clientId, secretId, endpointUrl }, []);
    if (resp.data.errors && resp.data.errors.length && resp.data.errors.length > 0) {
        const msg = `Failed to execute registerKintoneSetting API`;
        logger.api.error("Lantana-API", msg, resp.headers);
        throw new Error(msg);
    }

    logger.api.info("Lantana-API", "Succeed to request: registerKintoneSetting", resp.headers);

    return resp.data.data.registerKintoneSetting;
}

type IsKintoneTenantSetting = {
    isSetting: boolean;
}

/**
 * 呼び出したユーザーのテナントに紐づく管理者設定があるかを返す関数
 * サービス連携テーブル内に値があればtrueがかえって来るため、値の検証はできない
 */
export async function isKintoneTenantSetting(): Promise<boolean> {
    const logger = Log.getLogger();

    logger.api.info("Lantana-API", "Start to request: isKintoneTenantSetting");

    const resp = await query<IsKintoneTenantSetting>("isKintoneTenantSetting", ["isSetting"]);
    if (resp.data.errors && resp.data.errors.length && resp.data.errors.length > 0) {
        const msg = `Failed to execute isKintoneTenantSetting`;
        logger.api.error("Lantana-API", msg, resp.headers);
        return false
    }

    logger.api.info("Lantana-API", "Succeed to request: isKintoneTenantSetting", resp.headers);
    return resp.data.data.isKintoneTenantSetting.isSetting;
}

/**
*呼び出したユーザーのアクセストークン、リフレッシュトークンの有効期限を連携サービステーブルから取得する関数
*/
export async function linkServiceTokenExpiredDate(service: string): Promise<LinkServiceTokenExpiredDate> {
    const logger = Log.getLogger();

    logger.api.info("Lantana-API", "Start to request: linkServiceTokenExpiredDate");

    const resp = await query<LinkServiceTokenExpiredDate>("linkServiceTokenExpiredDate", "service", { service: service }, ["accessTokenExpiredDate", "refreshTokenExpiredDate"]);
    if (resp.data.errors && resp.data.errors.length && resp.data.errors.length > 0) {

        //DBにそもそもレコードがない場合は過去の日時を返す
        if (resp.data.errors[0].message === "A problem occurred while executing linkServiceTokenExpiredDate on type Query. Record is not found") {
            return defaultPastDate;
        }

        const msg = `Failed to execute linkServiceTokenExpiredDate API: id=${getEwbReqId(resp)}, service= ${service}`;
        logger.api.error("Lantana-API", msg, resp.headers);
        console.error(resp.data.errors)
        throw new Error(msg);
    }

    logger.api.info("Lantana-API", "Succeed to request: linkServiceTokenExpiredDate", resp.headers);

    return resp.data.data.linkServiceTokenExpiredDate
}

/**
 * 呼び出したユーザーの指定したサービスのトークンを連携サービステーブルから削除する関数
 */
export async function deleteLinkServiceToken(service: string): Promise<boolean> {
    const logger = Log.getLogger();

    logger.api.info("Lantana-API", "Start to request: deleteToken");

    const resp = await mutation<boolean>("deleteLinkServiceToken", "DeleteLinkServiceTokenInput", { service }, []);
    if (resp.data.errors && resp.data.errors.length && resp.data.errors.length > 0) {
        const msg = `Failed to execute deleteLinkServiceToken API: id=${getEwbReqId(resp)}, service= ${service}`;
        logger.api.error("Lantana-API", msg, resp.headers);
        throw new Error(msg);
    }

    logger.api.info("Lantana-API", "Succeed to request: deleteLinkServiceToken", resp.headers);

    return resp.data.data.deleteLinkServiceToken;
}

/**
 * 呼び出したユーザーのテナントに紐づくKintoneの設定情報（`endpointUrl`, `clientId`）を外部APIから取得する関数です。
 */
export async function kintoneSetting(): Promise<KintoneSetting> {
    const logger = Log.getLogger();

    logger.api.info("Lantana-API", "Start to request: kintoneSetting");

    const resp = await query<KintoneSetting>("kintoneSetting", ["endpointUrl", "clientId"]);
    if (resp.data.errors && resp.data.errors.length && resp.data.errors.length > 0) {
        console.error(resp.data.errors)
        const msg = `Failed to execute kintoneSetting API: id=${getEwbReqId(resp)}`;
        logger.api.error("Lantana-API", msg, resp.headers);
        return resp.data.data.kintoneSetting
    }

    logger.api.info("Lantana-API", "Succeed to request: kintoneSetting", resp.headers);

    return resp.data.data.kintoneSetting;
}


/**
 * リンクサービスのアクセストークンとリフレッシュトークンを生成する関数。
 * 指定されたサービスと認証コードを使用してトークンを生成し、返します。
 * 
 * @param {string} service - リンクするサービス名（例: "kintone" など）。
 * @param {string} code - OAuth認証フローで取得した認証コード。
 * @returns {Promise<LinkServiceTokenGeneratedFromCode>} トークンが生成された場合、`LinkServiceTokenGeneratedFromCode` 型のオブジェクトを含む Promise を返す。
 * @throws {Object} トークン生成に失敗した場合、エラーメッセージとサービス名を含むエラーオブジェクトをスローします。
 * 
 */
export async function linkServiceTokenGeneratedFromCode(service: string, code: string): Promise<LinkServiceTokenGeneratedFromCode> {
    const logger = Log.getLogger();
    logger.api.info("Lantana-API", "Start to request: linkServiceTokenGeneratedFromCode");

    const resp = await mutation<LinkServiceTokenGeneratedFromCode>("linkServiceTokenGeneratedFromCode", "LinkServiceTokenGeneratedFromCodeInput", { service, code }, ["accessToken { token expiredDate }", "refreshToken { token expiredDate }"]);
    if (resp.data.errors && resp.data.errors.length && resp.data.errors.length > 0) {
        const msg = `Failed to execute linkServiceTokenGeneratedFromCode API: id=${getEwbReqId(resp)}, service=${service}`;
        logger.api.error("Lantana-API", msg, resp.headers);
        console.error(resp.data.errors)
        throw new Error(msg);
    }

    logger.api.info("Lantana-API", "Succeed to request: linkServiceTokenGeneratedFromCode", resp.headers);

    return resp.data.data.linkServiceTokenGeneratedFromCode;
}

/**
 * サービスのアクセストークンとリフレッシュトークンを連携サービステーブルに登録する。
 * 指定されたサービス名、アクセストークン、リフレッシュトークンを使用してトークンを登録します。
 * 
 * @param {string} service - 登録するサービス名（例: "kintone" など）。
 * @param {LinkServiceToken} accessToken - 登録するアクセストークン。` LinkServiceToken` 型である必要があります。
 * @param {LinkServiceToken} refreshToken - 登録するリフレッシュトークン。` LinkServiceToken` 型である必要があります。
 * @returns {Promise<RegisterLinkServiceToken>} 登録が成功した場合、`RegisterLinkServiceToken` 型のオブジェクトを含む Promise を返す。
 * @throws {Object} 登録が失敗した場合、エラーメッセージとサービス名を含むエラーオブジェクトをスローします。
 * 
 */
export async function registerToken(service: string, accessToken: LinkServiceToken, refreshToken: LinkServiceToken): Promise<boolean> {
    const logger = Log.getLogger();

    logger.api.info("Lantana-API", "Start to request: registerToken");

    const resp = await mutation<boolean>("registerLinkServiceToken", "RegisterLinkServiceTokenInput", { service, accessToken, refreshToken }, []);
    if (resp.data.errors && resp.data.errors.length && resp.data.errors.length > 0) {
        const msg = `Failed to execute registerLinkServiceToken API: id=${getEwbReqId(resp)}, service=${service}`;
        logger.api.error("Lantana-API", msg, resp.headers);
        throw new Error(msg);
    }

    logger.api.info("Lantana-API", "Succeed to request: registerLinkServiceToken", resp.headers);

    return resp.data.data.registerLinkServiceToken;
}

/**
 * OAuthのstateを連携サービステーブルに保存する
 * stateの書式等のバリデーションは行わないため、LinkServiceSettingコンポーネントで定義する
 * 
 */
export async function registerOAuthState(state: string, service: string): Promise<boolean> {
    const logger = Log.getLogger();

    logger.api.info("Lantana-API", "Start to request: registerOAuthState");

    const resp = await mutation<boolean>("registerOAuthState", "RegisterOAuthStateInput", { state, service }, []);
    if (resp.data.errors && resp.data.errors.length && resp.data.errors.length > 0) {
        const msg = `Failed to execute registerOAuthState API: id=${getEwbReqId(resp)}, service=${service}`;
        logger.api.error("Lantana-API", msg, resp.headers);
        throw new Error(msg);
    }

    logger.api.info("Lantana-API", "Succeed to request: registerOAuthState", resp.headers);

    return resp.data.data.registerOAuthState;
}

type GetOAuthStateAndDelete = {
    state: string
}

/**
 * OAuthのstateを連携サービステーブルから取得する
 * 
 */
export async function getOAuthStateAndDelete(service: string): Promise<GetOAuthStateAndDelete> {
    const logger = Log.getLogger();

    logger.api.info("Lantana-API", "Start to request: getOAuthStateAndDelete");

    const resp = await mutation<GetOAuthStateAndDelete>("getOAuthStateAndDelete", "GetOAuthStateAndDeleteInput", { service }, ["state"]);
    if (resp.data.errors && resp.data.errors.length && resp.data.errors.length > 0) {
        const msg = `Failed to execute getOAuthStateAndDelete API: id=${getEwbReqId(resp)}`;
        logger.api.error("Lantana-API", msg, resp.headers);
        //エラーの場合は空文字を返す
        return { state: "" }
    }

    logger.api.info("Lantana-API", "Succeed to request: getOAuthStateAndDelete", resp.headers);

    return resp.data.data.getOAuthStateAndDelete;
}